import React, { useEffect, useState, useMemo, useContext } from 'react';
import clsx from 'clsx';
import { AuthContext } from '@/context/AuthContext';
import { useConfig } from '@/context/ConfigContext';
import './TaleemXBootSplash.css';

/** Single pulse / punch loop cycle duration in milliseconds (matches CSS keyframe period) */
export const CYCLE_MS = 1400;

/** Minimum number of full pulse / punch cycles to play before splash screen is eligible to exit */
export const MIN_PULSES = 3;

/** Default minimum splash duration (3 cycles × 1400ms = 4200ms) */
export const DEFAULT_MIN_DURATION = CYCLE_MS * MIN_PULSES;

/** Default exit transition duration in milliseconds */
export const DEFAULT_EXIT_DURATION = 820;

export interface TaleemXBootSplashProps {
  /** Minimum time (ms) to keep loading loop visible before exit. Default: DEFAULT_MIN_DURATION (4200ms = 3 full pulses) */
  minDuration?: number;
  /** Exit animation duration (ms). Default: DEFAULT_EXIT_DURATION (820ms) */
  exitDuration?: number;
  /** Explicit override for readiness (e.g. for testing / previews). If specified as boolean, overrides both timer and data loading */
  forceReady?: boolean;
  /** Callback fired once splash fully unmounts */
  onFinish?: () => void;
  /** Optional custom class */
  className?: string;
}

// Geometry paths from TaleemXMark (exact match, no redrawn geometry)
const PATH_TOP_BAR =
  'M4400 5948 c-49 -25 -73 -69 -73 -135 0 -59 16 -90 67 -127 27 -21 38 -21 719 -24 l692 -2 42 27 c42 27 57 51 67 110 11 66 -40 148 -98 158 -17 3 -335 6 -706 7 -625 3 -678 2 -710 -14z';

const PATH_TOP_LEFT =
  'M3865 5910 c-3 -5 -17 -10 -30 -10 -44 0 -90 -31 -130 -86 -49 -67 -60 -116 -45 -190 14 -66 50 -112 238 -298 112 -111 132 -127 149 -118 10 6 61 55 113 109 99 103 120 141 120 218 0 81 -20 115 -140 236 -102 102 -121 117 -171 132 -64 19 -95 21 -104 7z';

const PATH_BOTTOM_LEFT =
  'M4397 5514 c-4 -4 -7 -14 -7 -22 0 -8 -10 -42 -22 -76 -21 -58 -39 -78 -339 -378 -185 -185 -329 -337 -346 -365 -23 -40 -28 -59 -27 -113 1 -76 16 -112 73 -165 80 -76 182 -95 276 -50 48 23 665 631 665 655 0 9 3 20 8 24 13 13 28 100 21 121 -4 11 -13 40 -20 64 -10 35 -40 71 -144 177 -72 74 -134 131 -138 128z';

const PATH_TOP_RIGHT =
  'M6242 5892 c-72 -37 -644 -605 -672 -668 -27 -59 -26 -144 2 -203 22 -45 256 -291 277 -291 6 0 12 10 13 23 2 31 3 38 16 70 7 16 12 33 12 38 0 5 5 9 10 9 6 0 13 8 17 18 3 9 148 159 323 332 174 173 325 331 335 350 22 43 22 168 -2 217 -23 49 -96 109 -148 122 -72 18 -125 13 -183 -17z';

const PATH_BOTTOM_RIGHT =
  'M6161 4994 c-25 -25 -62 -59 -83 -77 -21 -17 -54 -57 -73 -88 l-35 -58 9 -86 c12 -100 17 -109 133 -221 134 -129 193 -156 292 -130 64 17 112 48 151 101 39 50 50 173 23 230 -20 40 -344 375 -363 375 -6 0 -30 -20 -54 -46z';

const PATH_DOT =
  'M5027 5372 c-21 -9 -35 -21 -31 -25 5 -5 2 -5 -7 -2 -17 6 -62 -32 -53 -46 3 -5 0 -8 -8 -7 -7 2 -12 -3 -10 -9 1 -7 -4 -12 -10 -10 -7 1 -12 -6 -12 -15 1 -10 -2 -16 -6 -13 -12 7 -29 -41 -36 -99 -7 -60 19 -161 38 -150 6 4 8 2 4 -4 -8 -14 43 -74 56 -65 7 3 8 1 4 -5 -9 -14 39 -46 55 -36 7 4 9 3 6 -3 -4 -6 12 -16 38 -23 52 -15 132 -7 179 17 39 20 77 54 71 64 -3 4 0 11 6 15 8 5 9 3 4 -7 -8 -12 -6 -12 8 -1 9 8 26 34 38 60 33 67 32 161 -1 227 -14 28 -30 49 -37 48 -6 -1 -17 5 -25 15 -12 15 -11 16 5 3 10 -8 17 -9 17 -3 0 17 -58 54 -71 46 -7 -4 -9 -3 -6 3 21 34 -154 54 -216 25z m170 -498 c-3 -3 -12 -4 -19 -1 -8 3 -5 6 6 6 11 1 17 -2 13 -5z';

/**
 * TaleemXBootSplash - Startup and loading splash screen.
 *
 * Requirements & Motion Story:
 * 1. Loop: Side arrows gather smoothly inward; center dot punches outward with continuous, soft bloom (≥3 pulses).
 * 2. Gate: Whichever is longer wins — never exits before 3 full punch cycles (4200ms) AND never exits while bootstrap data is loading.
 * 3. Exit: Center dot pulses outward, flying side capsules and top bar outward to fade;
 *          middle dot expands into a circular reveal aperture displaying the application underneath.
 */
export const TaleemXBootSplash: React.FC<TaleemXBootSplashProps> = ({
  minDuration = DEFAULT_MIN_DURATION,
  exitDuration = DEFAULT_EXIT_DURATION,
  forceReady,
  onFinish,
  className,
}) => {
  const [phase, setPhase] = useState<'loading' | 'exiting' | 'finished'>('loading');
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  // AuthContext null-safe if rendered outside AuthProvider (tests)
  const auth = useContext(AuthContext);
  const authLoading = auth?.isLoading ?? false;
  const { isLoading: configLoading } = useConfig();

  // Check if reduced motion is requested
  const isReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  }, []);

  // Minimum splash display timer (≥3 full pulses: default 4200ms)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setMinTimeElapsed(true);
    }, minDuration);
    return () => window.clearTimeout(timer);
  }, [minDuration]);

  // Transition to exiting once minimum time elapsed AND all bootstrap data ready (auth + config)
  useEffect(() => {
    if (phase !== 'loading') return;

    const isReady =
      forceReady !== undefined ? forceReady : minTimeElapsed && !authLoading && !configLoading;

    if (isReady) {
      setPhase('exiting');
    }
  }, [phase, minTimeElapsed, authLoading, configLoading, forceReady]);

  // Clean unmount after exit animation completes
  useEffect(() => {
    if (phase !== 'exiting') return;

    const actualExitTime = isReducedMotion ? 250 : exitDuration;
    const timer = window.setTimeout(() => {
      setPhase('finished');
      onFinish?.();
    }, actualExitTime);

    return () => window.clearTimeout(timer);
  }, [phase, exitDuration, isReducedMotion, onFinish]);

  if (phase === 'finished') {
    return null;
  }

  return (
    <div
      role="status"
      aria-label="TaleemX loading"
      data-testid="taleemx-boot-splash"
      data-phase={phase}
      className={clsx('taleemx-boot-splash', className)}
    >
      <div className="taleemx-splash-stage">
        {/* Expanding circle background layer for center dot reveal */}
        <div className="taleemx-splash-expanding-dot" aria-hidden="true" />

        {/* Traced Vector Logo Mark */}
        <svg
          viewBox="0 0 1024 1024"
          className="taleemx-splash-mark"
          data-testid="taleemx-splash-mark"
          role="img"
          aria-label="TaleemX Logo"
        >
          {/* Top Bar Capsule */}
          <g
            className="taleemx-splash-top-group"
            data-testid="taleemx-splash-top"
          >
            <g transform="translate(0,1024) scale(0.1,-0.1)" fill="#2A2A2A" stroke="none">
              <path d={PATH_TOP_BAR} />
            </g>
          </g>

          {/* Left Arrow Pair (Top-Left & Bottom-Left) */}
          <g
            className="taleemx-splash-left-group"
            data-testid="taleemx-splash-left"
          >
            <g transform="translate(0,1024) scale(0.1,-0.1)" fill="#2A2A2A" stroke="none">
              <path d={PATH_TOP_LEFT} />
              <path d={PATH_BOTTOM_LEFT} />
            </g>
          </g>

          {/* Right Arrow Pair (Top-Right & Bottom-Right) */}
          <g
            className="taleemx-splash-right-group"
            data-testid="taleemx-splash-right"
          >
            <g transform="translate(0,1024) scale(0.1,-0.1)" fill="#2A2A2A" stroke="none">
              <path d={PATH_TOP_RIGHT} />
              <path d={PATH_BOTTOM_RIGHT} />
            </g>
          </g>

          {/* Center Dot */}
          <g
            className="taleemx-splash-dot-group"
            data-testid="taleemx-splash-dot"
          >
            <g transform="translate(0,1024) scale(0.1,-0.1)" fill="#60A5FA" stroke="none">
              <path d={PATH_DOT} />
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default TaleemXBootSplash;
